import React from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  Outlet,
  useParams,
} from "react-router-dom";

import Base from "./components/layouts/Base.jsx";

/* 메인 */
import Main from "./components/main/main/Main";

/* 총학생회 */
import StdCouncil from "./components/stdcouncil";
import Council from "./components/stdcouncil/council/council.jsx";
import Organization from "./components/stdcouncil/organization/organization.jsx";
import Map from "./components/stdcouncil/map/map.jsx";

/* 소통 */
import Ask from "./components/ask/ask/Ask";
import Faq from "./components/ask/faq/Faq";

/* 약관 */
import TermsOfService from "./components/policy/TermsOfService";
import PrivacyPolicy from "./components/policy/PrivacyPolicy";

/* 총학생회 활동 */
import StdCouncilLog from "./components/stdcouncil-log";
import Notice from "./components/stdcouncil-log/Notice/Notice";
import TemplateDetail from "./components/stdcouncil-log/Notice/Template/TemplateDetail";
import Event from "./components/stdcouncil-log/Event/Event";
import Partnership from "./components/stdcouncil-log/Partnership/Partnership";
import Calendar from "./components/stdcouncil-log/Calendar/Calendar";

/* 중운위 */
import CC from "./components/cc";
import CcUnit from "./components/cc/CcUnit/CcUnit.jsx";

/* 문서 */
import Documents from "./components/documents";
import StudentCouncil from "./components/documents/student-council/student-council";
import CentralCommittee from "./components/documents/central-committee/central-committee";
import AuditCommittee from "./components/documents/audit-committee/audit-committee";
import Others from "./components/documents/others/others";
import Delegate from "./components/documents/delegate/delegate";

/* 관리자 */
import Login from "./components/admin/Login";

import AdminLayout from "./components/admin/layout/AdminLayout";
import {
  getAdminHomePath,
  getStoredAdminInfo,
} from "./components/admin/AdminAuth";

import IntroAdmin from "./components/admin/intro/IntroAdmin";
import CcIntroAdmin from "./components/admin/cc/CcIntroAdmin";
import CcBoard from "./components/admin/cc/CcBoard";

import OrgExecutive from "./components/admin/organization/OrgExecutive";
import OrgOffice from "./components/admin/organization/OrgOffice";
import OrgBoardAdmin from "./components/admin/organization/OrgBoardAdmin";
import OrgBoardForm from "./components/admin/organization/OrgBoardForm";

import ContentList from "./components/admin/contents/ContentList";
import ContentWrite from "./components/admin/contents/ContentWrite";
import ContentEdit from "./components/admin/contents/ContentEdit";

import Account from "./components/admin/account/Account";

import DataAdmin from "./components/admin/document/DataAdmin";
import DataForm from "./components/admin/document/DataForm";

import SiteAdmin from "./components/admin/site/SiteAdmin";
import PermissionAdmin from "./components/admin/permission/PermissionAdmin.jsx";

function ProtectedAdminRoute({ allowedTypes, children }) {
  const token = localStorage.getItem("accessToken");
  const { adminType } = getStoredAdminInfo();

  if (!token) {
    return <Navigate to="/stdcounciladmin" replace />;
  }

  if (!allowedTypes.includes(adminType)) {
    return <Navigate to={getAdminHomePath()} replace />;
  }

  return children;
}

function ProtectedOutlet({ allowedTypes }) {
  return (
    <ProtectedAdminRoute allowedTypes={allowedTypes}>
      <Outlet />
    </ProtectedAdminRoute>
  );
}

function AdminHomeIndexRoute() {
  const { isStdCouncil, isCc, isAudit } = getStoredAdminInfo();

  if (isStdCouncil) {
    return <Navigate to="intro/intro/current/student" replace />;
  }

  if (isCc) {
    return <Navigate to="intro" replace />;
  }

  if (isAudit) {
    return <Navigate to="data/audit-committee" replace />;
  }

  return <Navigate to="/stdcounciladmin" replace />;
}

function AdminIntroIndexRoute() {
  const { isStdCouncil, isCc } = getStoredAdminInfo();

  if (isStdCouncil) {
    return <Navigate to="intro/current/student" replace />;
  }

  if (isCc) {
    return <CcIntroAdmin />;
  }

  return <Navigate to={getAdminHomePath()} replace />;
}

function AdminIntroTypeRoute() {
  const { isStdCouncil, isCc } = getStoredAdminInfo();

  if (isStdCouncil) {
    return <Navigate to="current/student" replace />;
  }

  if (isCc) {
    return <CcIntroAdmin />;
  }

  return <Navigate to={getAdminHomePath()} replace />;
}

function AdminIntroRoute() {
  const { isStdCouncil, isCc } = getStoredAdminInfo();

  if (isStdCouncil) {
    return <IntroAdmin />;
  }

  if (isCc) {
    return <CcIntroAdmin />;
  }

  return <Navigate to={getAdminHomePath()} replace />;
}

function AdminDataIndexRoute() {
  const { isStdCouncil, isAudit } = getStoredAdminInfo();

  if (isStdCouncil) {
    return <Navigate to="stdcouncil" replace />;
  }

  if (isAudit) {
    return <Navigate to="audit-committee" replace />;
  }

  return <Navigate to={getAdminHomePath()} replace />;
}

function AdminDataCategoryRoute({ children }) {
  const { category } = useParams();
  const { isStdCouncil, isAudit } = getStoredAdminInfo();

  if (isAudit && category !== "audit-committee") {
    return (
      <Navigate
        to="/stdcounciladmin/adminhome/data/audit-committee"
        replace
      />
    );
  }

  if (!isStdCouncil && !isAudit) {
    return <Navigate to={getAdminHomePath()} replace />;
  }

  return children;
}

function AppRoutes() {
  const location = useLocation();

  const state =
    location.state && location.state.backgroundLocation
      ? { backgroundLocation: location.state.backgroundLocation }
      : null;

  return (
    <>
      <Routes location={state?.backgroundLocation || location}>
        {/* 메인 */}
        <Route element={<Base showHero={false} />}>
          <Route path="/" element={<Main />} />
        </Route>

        {/* 소통 */}
        <Route element={<Base title="소통" useCouncilIntroHero={true} />}>
          <Route path="/ask" element={<Ask />} />
          <Route path="/faq" element={<Faq />} />
        </Route>

        {/* 총학생회 */}
        <Route element={<Base title="총학생회" useCouncilIntroHero={true} />}>
          <Route path="/stdcouncil" element={<StdCouncil />} />
          <Route path="/stdcouncil/council" element={<Council />} />
          <Route path="/stdcouncil/organization" element={<Organization />} />
          <Route path="/stdcouncil/map" element={<Map />} />
        </Route>

        {/* 총학생회 활동 */}
        <Route
          element={
            <Base title="총학생회 활동" useCouncilIntroHero={true} />
          }
        >
          <Route path="/stdcouncil-log" element={<StdCouncilLog />}>
            <Route index element={<Notice />} />
            <Route path="Notice" element={<Notice />} />
            <Route path="Notice/:id" element={<TemplateDetail />} />
            <Route path="Event" element={<Event />} />
            <Route path="Partnership" element={<Partnership />} />
            <Route path="Partnership/:id" element={<Partnership />} />
            <Route path="Calendar" element={<Calendar />} />
          </Route>
        </Route>

        {/* 문서 */}
        <Route element={<Base title="자료실" />}>
          <Route path="/documents" element={<Documents />}>
            <Route index element={<StudentCouncil />} />
            <Route path="student-council" element={<StudentCouncil />} />
            <Route
              path="student-council/regulations/:id"
              element={<StudentCouncil />}
            />
            <Route
              path="student-council/template/:id"
              element={<StudentCouncil />}
            />

            <Route path="central-committee" element={<CentralCommittee />} />
            <Route path="central-committee/:id" element={<CentralCommittee />} />

            <Route path="audit-committee" element={<AuditCommittee />} />
            <Route path="audit-committee/:id" element={<AuditCommittee />} />

            <Route path="others" element={<Others />} />
            <Route path="others/:id" element={<Others />} />

            <Route path="delegate" element={<Delegate />} />
            <Route path="delegate/:id" element={<Delegate />} />
          </Route>
        </Route>

        {/* 중운위 */}
        <Route element={<Base title="중앙운영위원회" />}>
          <Route path="/cc" element={<CC />}>
            <Route index element={<Navigate to="Scitech" replace />} />
            <Route path=":unit" element={<CcUnit />} />
            <Route path=":unit/:id" element={<CcUnit />} />
          </Route>
        </Route>

        {/* 정책 */}
        <Route element={<Base />}>
          <Route path="/policy/terms" element={<TermsOfService />} />
          <Route path="/policy/privacy" element={<PrivacyPolicy />} />
        </Route>

        {/* 로그인 */}
        <Route path="/stdcounciladmin" element={<Login />} />

        {/* 관리자 */}
        <Route
          path="/stdcounciladmin/adminhome"
          element={
            <ProtectedAdminRoute allowedTypes={["stdcouncil", "cc", "audit"]}>
              <AdminLayout />
            </ProtectedAdminRoute>
          }
        >
          <Route index element={<AdminHomeIndexRoute />} />

          {/* 소개 관리 - 총학생회/단대/동연 */}
          <Route
            path="intro"
            element={<ProtectedOutlet allowedTypes={["stdcouncil", "cc"]} />}
          >
            <Route index element={<AdminIntroIndexRoute />} />
            <Route path=":type" element={<AdminIntroTypeRoute />} />
            <Route path=":type/:mode/:orgType" element={<AdminIntroRoute />} />
          </Route>

          {/* 조직도 관리 - 총학생회만 */}
          <Route
            path="organization"
            element={<ProtectedOutlet allowedTypes={["stdcouncil"]} />}
          >
            <Route index element={<Navigate to="exec" replace />} />
            <Route path="exec" element={<OrgExecutive />} />
            <Route path="office" element={<OrgOffice />} />
          </Route>

          {/* 콘텐츠 관리 - 총학생회만 */}
          <Route
            path="contents"
            element={<ProtectedOutlet allowedTypes={["stdcouncil"]} />}
          >
            <Route index element={<Navigate to="notice" replace />} />
            <Route path=":category" element={<ContentList />} />
            <Route path=":category/write" element={<ContentWrite />} />
            <Route path=":category/edit/:id" element={<ContentEdit />} />
          </Route>

          {/* 총학생회 관리자 - 조직별 관리 */}
          <Route
            path="group"
            element={<ProtectedOutlet allowedTypes={["stdcouncil"]} />}
          >
            <Route index element={<Navigate to="scitech" replace />} />
            <Route path=":unit" element={<OrgBoardAdmin />} />
            <Route path=":unit/write" element={<OrgBoardForm mode="write" />} />
            <Route
              path=":unit/edit/:id"
              element={<OrgBoardForm mode="edit" />}
            />
          </Route>

          {/* 단과대/동연 관리자 - 조직별 관리 */}
          <Route
            path="board"
            element={<ProtectedOutlet allowedTypes={["cc"]} />}
          >
            <Route index element={<CcBoard />} />
            <Route path="write" element={<OrgBoardForm mode="write" />} />
            <Route path="edit/:id" element={<OrgBoardForm mode="edit" />} />
          </Route>

          {/* 자료실 관리 - 총학생회/감사위원회 */}
          <Route
            path="data"
            element={<ProtectedOutlet allowedTypes={["stdcouncil", "audit"]} />}
          >
            <Route index element={<AdminDataIndexRoute />} />

            <Route
              path=":category"
              element={
                <AdminDataCategoryRoute>
                  <DataAdmin />
                </AdminDataCategoryRoute>
              }
            />

            <Route
              path=":category/write"
              element={
                <AdminDataCategoryRoute>
                  <DataForm mode="write" />
                </AdminDataCategoryRoute>
              }
            />

            <Route
              path=":category/edit/:id"
              element={
                <AdminDataCategoryRoute>
                  <DataForm mode="edit" />
                </AdminDataCategoryRoute>
              }
            />
          </Route>

          {/* 내 계정 설정 - 전체 관리자 */}
          <Route
            path="account"
            element={
              <ProtectedAdminRoute allowedTypes={["stdcouncil", "cc", "audit"]}>
                <Account />
              </ProtectedAdminRoute>
            }
          />

          {/* 권한 관리 - 총학생회만 */}
          <Route
            path="permission"
            element={
              <ProtectedAdminRoute allowedTypes={["stdcouncil"]}>
                <PermissionAdmin />
              </ProtectedAdminRoute>
            }
          />

          {/* 사이트 관리 - 총학생회만 */}
          <Route
            path="site"
            element={<ProtectedOutlet allowedTypes={["stdcouncil"]} />}
          >
            <Route index element={<Navigate to="contact" replace />} />
            <Route path=":menu" element={<SiteAdmin />} />
          </Route>
        </Route>

        {/* 예외 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {state?.backgroundLocation && (
        <Routes>
          <Route path="/policy/terms" element={<TermsOfService />} />
          <Route path="/policy/privacy" element={<PrivacyPolicy />} />
        </Routes>
      )}
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}